import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  MFA_OAUTH_URL,
  MULTI_FACTOR_EndpointKey
} from '../../../../constants/login/login-remember-me-constant';
import { HttpRequestMethods, IDP_APP } from '../../../../constants/utils';
import {
  LoginAuthorizationOperationTypes,
  LoginOAuthParams,
  LoginOAuthResponse
} from '../../../../models/login';
import { HttpHeadersPayload, ReplaceItem } from '../../../../models/utils';
import { X_EAM_AUTHN_STATE } from '../../../../types/constants';
import { LoginRememberMeUtilsService } from '../../../../utils/login/login-remember-me-utils.service';
import { ApiBuilder, AppConfigService, handleError } from '../../../utils';

@Injectable({
  providedIn: 'root'
})
export class LoginAuthorizationService {
  _errorHandler = handleError.bind(this);

  constructor(
    private http: HttpClient,
    private utils: LoginRememberMeUtilsService,
    private appConfig: AppConfigService
  ) {}

  getEamHeaderNameEnv(loginOAuthParams: LoginOAuthParams): string {
    if (
      loginOAuthParams.oauthConfig &&
      loginOAuthParams.oauthConfig.X_EAM_AUTHN_STATE_ENV
    )
      return loginOAuthParams.oauthConfig.X_EAM_AUTHN_STATE_ENV;
    else return X_EAM_AUTHN_STATE;
  }

  loginAuthorization = (
    loginOAuthParams: LoginOAuthParams,
    eamAuthStateValue?: string,
    forcePromptLoginFlow = false
  ): Observable<LoginOAuthResponse> => {
    let initiateAuthorizationHeader: HttpHeadersPayload = {};
    if (eamAuthStateValue && eamAuthStateValue !== '') {
      const eamHeaderName = this.getEamHeaderNameEnv(loginOAuthParams);
      initiateAuthorizationHeader = {
        'Content-Type':
          'application/vnd.pingidentity.initiateOAuthAuthorization+json',
        'X-XSRF-Header': 'PingFederate',
        [eamHeaderName]: eamAuthStateValue,
        rejectUnauthorized: 'false'
      };
    } else {
      initiateAuthorizationHeader = {
        'Content-Type':
          'application/vnd.pingidentity.initiateOAuthAuthorization+json',
        'X-XSRF-Header': 'PingFederate',
        rejectUnauthorized: 'false'
      };
    }

    const loginAuthorizationUrl: ReplaceItem[] = [
      {
        src: MFA_OAUTH_URL,
        target: this.getUrlBasedOnStorageValue(
          loginOAuthParams,
          forcePromptLoginFlow
        )
      }
    ];

    return new ApiBuilder<undefined, LoginOAuthResponse>(
      this.http,
      this.appConfig
    )
      .app(IDP_APP.COMMON)
      .endpointKey(MULTI_FACTOR_EndpointKey.MFA_OAUTH_URL)
      .overrideEndpoint(loginAuthorizationUrl)
      .method(HttpRequestMethods['GET_HEADERS'])
      .timeOut(loginOAuthParams.oauthConfig.LOGIN_TIMEOUT ?? 10000)
      .httpHeaders(initiateAuthorizationHeader)
      .errorHandler(this._errorHandler)
      .library('LOGIN_AUTHORIZATION_SERVICE')
      .withCredentials(true)
      .create();
  };

  getUrlBasedOnStorageValue(
    loginOAuthParams: LoginOAuthParams,
    forcePromptLoginFlow = false
  ): string {
    const encryptedTokenStored = this.utils.getLocalStorageValue('encyToken');
    const uniqueIdentifierStored = this.utils.getLocalStorageValue('uid');
    if (encryptedTokenStored && uniqueIdentifierStored) {
      if (loginOAuthParams.prompt) {
        return this.getLoginAuthorizationUrl(loginOAuthParams, 'PROMPT');
      } else {
        return this.getLoginAuthorizationUrl(loginOAuthParams, 'DEFAULT');
      }
    }
    return this.getUrlBasedOnStorageValueHelper(
      loginOAuthParams,
      forcePromptLoginFlow
    );
  }

  getUrlBasedOnStorageValueHelper(
    loginOAuthParams: LoginOAuthParams,
    forcePromptLoginFlow = false
  ): string {
    if (forcePromptLoginFlow) {
      return this.getLoginAuthorizationUrl(loginOAuthParams, 'PROMPT');
    } else {
      return this.getLoginAuthorizationUrl(loginOAuthParams, 'DEFAULT');
    }
  }

  getLoginAuthorizationUrl(
    loginOAuthParams: LoginOAuthParams,
    operationType: LoginAuthorizationOperationTypes
  ): string {
    const initiateSsoSessionUrl = {
      PROMPT: this.utils.getPingServiceUrl(
        loginOAuthParams.oauthConfig,
        '',
        loginOAuthParams.pkceCode.codeChallenge
      ).loginOAuthUrlPersistantRememberMe,
      DEFAULT: this.utils.getPingServiceUrl(
        loginOAuthParams.oauthConfig,
        '',
        loginOAuthParams.pkceCode.codeChallenge
      ).loginOAuthUrlRememberMe
    };
    return initiateSsoSessionUrl[operationType];
  }
}
